import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LogOut,
  Home,
  FolderOpen,
  Package,
  User,
  Shield,
  ShoppingCart,
  MessageCircle,
  Menu,
  X,
  Store,
} from "lucide-react";
import {
  logout,
  canAccessProducts,
  canAccessCategories,
  canManageAdmins,
  canManageShops,
  canAccessOrders,
  canAccessUsers,
  canAccessChat,
  canAccessComments,
  isShop,
  getCurrentUser,
} from "../lib/pocketbase";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen]);

  const currentUser = getCurrentUser() as {
    email?: string;
    title?: string;
  } | null;
  const panelTitle = isShop() ? "پنل فروشگاه" : "پنل مدیریت";
  const userLabel = isShop()
    ? currentUser?.title || currentUser?.email || "فروشنده"
    : "مدیر سیستم";

  const navigation = [
    { name: "داشبورد", href: "/", icon: Home, show: true },
    {
      name: "دسته‌بندی‌ها",
      href: "/categories",
      icon: FolderOpen,
      show: canAccessCategories(),
    },
    {
      name: "محصولات",
      href: "/products",
      icon: Package,
      show: canAccessProducts(),
    },
    {
      name: "سفارشات",
      href: "/orders",
      icon: ShoppingCart,
      show: canAccessOrders(),
    },
    { name: "کاربران", href: "/users", icon: User, show: canAccessUsers() },
    {
      name: "نظرات",
      href: "/comments",
      icon: MessageCircle,
      show: canAccessComments(),
    },
    { name: "چت", href: "/chat", icon: MessageCircle, show: canAccessChat() },
    { name: "مدیران", href: "/admins", icon: Shield, show: canManageAdmins() },
    {
      name: "فروشگاه‌ها",
      href: "/shops",
      icon: Store,
      show: canManageShops(),
    },
  ];

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
        <h1 className="text-xl font-bold text-primary-600">{panelTitle}</h1>
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          aria-label="بستن منو"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigation
          .filter((item) => item.show)
          .map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <item.icon className="ml-3 h-5 w-5 shrink-0" />
                {item.name}
              </Link>
            );
          })}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
              <User className="h-4 w-4 text-primary-600" />
            </div>
            <div className="mr-3 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {userLabel}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 shrink-0"
            title="خروج"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between h-14 px-4 bg-white border-b border-gray-200">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="p-2 -mr-2 text-gray-600 hover:text-gray-900 rounded-lg"
          aria-label="باز کردن منو"
        >
          <Menu className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-bold text-primary-600">{panelTitle}</h1>
        <div className="w-10" />
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"
        }`}
      >
        {sidebar}
      </aside>

      {/* Main content */}
      <div className="lg:mr-64">
        <main className="p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
